import { sanitizeAuthNextPath, type AuthLogoutResponse } from '@helix/lib';
import { logout } from './authApi';
import { ASK_TURNS_STORAGE_KEY_PREFIX } from './ask-turns-storage';
import { storePostLogoutState } from './logout-state';

type SignOutOptions = {
  next?: string | null;
  assign?: (url: string) => void;
};

export const SIGN_OUT_ERROR_MESSAGE = 'Sign out failed. Please try again.';

// #5100 UI-1i P1 — the /ask surface retains its conversation per identity under the
// neutral shared prefix. A signed-out tab must never leave a prior identity's turns
// readable by whoever uses it next.

/** Every sessionStorage key for the /ask surface, whichever identity wrote it. */
function clearAskTurnsSessionStorage(): void {
  try {
    const keys: string[] = [];
    for (let index = 0; index < window.sessionStorage.length; index += 1) {
      const key = window.sessionStorage.key(index);
      if (
        key === ASK_TURNS_STORAGE_KEY_PREFIX ||
        key?.startsWith(`${ASK_TURNS_STORAGE_KEY_PREFIX}:`)
      ) {
        keys.push(key);
      }
    }
    for (const key of keys) window.sessionStorage.removeItem(key);
  } catch {
    // Ignore best-effort browser cleanup failures.
  }
}

export function clearBrowserSignOutState(): void {
  try {
    window.localStorage.removeItem('tenant_id');
    window.localStorage.removeItem('user_id');
    document.cookie = 'helix_e2e_tenant_id=; Path=/; Max-Age=0; SameSite=Lax';
    document.cookie = 'helix_e2e_user_id=; Path=/; Max-Age=0; SameSite=Lax';
  } catch {
    // Ignore best-effort browser cleanup failures.
  }
  clearAskTurnsSessionStorage();
}

export async function signOut(options: SignOutOptions = {}): Promise<AuthLogoutResponse> {
  const nextPath = sanitizeAuthNextPath(options.next);
  const assign = options.assign ?? ((url: string) => window.location.assign(url));
  const response = await logout(nextPath);

  clearBrowserSignOutState();
  storePostLogoutState(nextPath);
  assign(response.providerLogoutUrl ?? response.redirectTo);
  return response;
}
